import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Row, Col, Card, CardHeader, CardBody, Badge, Button, Table } from 'reactstrap';
import moment from 'moment';
import { getMyAccountRequestById, AccountRequestDetailData } from 'features/matriculates/matriculatesAPI';

const stateLabels: Record<number, { label: string; color: string }> = {
  1: { label: 'Solicitada', color: 'info' },
  2: { label: 'Aceptada', color: 'success' },
  3: { label: 'Rechazada', color: 'danger' },
};

interface FieldRow {
  label: string;
  value: string | number | null | undefined;
}

const buildFieldRows = (data: AccountRequestDetailData): FieldRow[] => [
  { label: 'Email', value: data.email },
  {
    label: 'Celular',
    value: data.celular_codigo || data.celular_numero
      ? `${ data.celular_codigo ?? '' } ${ data.celular_numero ?? '' }`.trim()
      : null,
  },
  {
    label: 'Domicilio particular',
    value: [
      data.particular_calle,
      data.particular_numero,
      data.particular_piso ? `Piso ${ data.particular_piso }` : null,
      data.particular_depto ? `Depto ${ data.particular_depto }` : null,
      data.particular_cp ? `CP ${ data.particular_cp }` : null,
      data.particular_localidad,
      data.particular_partido,
    ].filter(Boolean).join(', ') || null,
  },
  {
    label: 'Teléfono particular',
    value: data.particular_telefono_codigo || data.particular_telefono_numero
      ? `${ data.particular_telefono_codigo ?? '' } ${ data.particular_telefono_numero ?? '' }`.trim()
      : null,
  },
  {
    label: 'Domicilio legal',
    value: [
      data.legal_calle,
      data.legal_numero,
      data.legal_piso ? `Piso ${ data.legal_piso }` : null,
      data.legal_depto ? `Depto ${ data.legal_depto }` : null,
      data.legal_cp ? `CP ${ data.legal_cp }` : null,
      data.legal_localidad,
      data.legal_partido,
    ].filter(Boolean).join(', ') || null,
  },
  {
    label: 'Teléfono legal',
    value: data.legal_telefono_codigo || data.legal_telefono_numero
      ? `${ data.legal_telefono_codigo ?? '' } ${ data.legal_telefono_numero ?? '' }`.trim()
      : null,
  },
];

const emptyData: AccountRequestDetailData = {
  id: 0,
  state_id: 0,
  email: null,
  celular_codigo: null,
  celular_numero: null,
  particular_calle: null,
  particular_numero: null,
  particular_piso: null,
  particular_depto: null,
  particular_cp: null,
  particular_localidad: null,
  particular_partido: null,
  particular_telefono_codigo: null,
  particular_telefono_numero: null,
  legal_calle: null,
  legal_numero: null,
  legal_piso: null,
  legal_depto: null,
  legal_cp: null,
  legal_localidad: null,
  legal_partido: null,
  legal_telefono_codigo: null,
  legal_telefono_numero: null,
  created_at: null,
  resolved_at: null,
  rejection_text: null,
};

export default function AccountRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [data, setData] = useState<{
    new: AccountRequestDetailData | null;
    old: AccountRequestDetailData | null;
  }>({ new: null, old: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    getMyAccountRequestById(id)
      .then(setData)
      .catch(err => {
        console.error(err);
        setError('No se pudo recuperar el pedido.');
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    console.debug('AccountRequestDetail data:', data);
  }, [data]);

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col md={8} style={{ fontSize: '1.5rem' }}>
                  Pedido de cambio de datos #{ id }
                </Col>
                <Col md={4} className="text-right">
                  <Button color="secondary" onClick={() => history.goBack()}>Volver</Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              { isLoading && <p>Cargando…</p> }
              { !isLoading && error && (
                <>
                  <p className="text-danger">{ error }</p>
                  <Link to="/mi-cuenta">
                    <Button color="primary">Ir a Mi Cuenta</Button>
                  </Link>
                </>
              )}
              { !isLoading && !error && data && (
                <>
                  <Row>
                    <Col md={4}>
                      <strong>Estado:</strong>{' '}
                      { (() => {
                        const s = stateLabels[data.new?.state_id || 0] ?? { label: '-', color: 'secondary' };
                        return <Badge color={s.color}>{ s.label }</Badge>;
                      })() }
                    </Col>
                    <Col md={4}>
                      <strong>Fecha del pedido:</strong>{' '}
                      { data.new?.created_at ? moment(data.new?.created_at).format('DD/MM/YYYY HH:mm') : '-' }
                    </Col>
                    <Col md={4}>
                      <strong>Fecha de resolución:</strong>{' '}
                      { data.new?.resolved_at ? moment(data.new?.resolved_at).format('DD/MM/YYYY HH:mm') : '-' }
                    </Col>
                  </Row>
                  { data.new?.state_id === 3 && data.new?.rejection_text && (
                    <div className="alert alert-warning mt-3" role="alert">
                      <strong>Motivo de rechazo:</strong> { data.new?.rejection_text }
                    </div>
                  )}
                  <h5 className="mt-4">Datos solicitados</h5>
                  <Table responsive>
                    <tbody>
                      { buildFieldRows(data.new || emptyData).map(row => (
                        <tr key={row.label}>
                          <th style={{ width: '30%' }}>{ row.label }</th>
                          <td>{ row.value ? String(row.value) : <em className="text-muted">No modificado</em> }</td>
                        </tr>
                      )) }
                    </tbody>
                  </Table>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
